import { useState, useCallback } from "react";
import { useGetList, useUpdate, useCreate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { GitBranch, Plus, CheckCircle2, XCircle, DollarSign, Calendar } from "lucide-react";
import { ChangeOrderForm } from "./ChangeOrderForm";

interface ChangeOrder {
  id: string;
  co_number: string;
  status: string;
  title: string;
  description: string;
  reason: string;
  cost_change: number;
  new_total: number;
  schedule_impact_days: number;
  approved_at: string | null;
  declined_at: string | null;
  created_at: string;
}

interface ChangeOrderListProps {
  jobId: string;
  contactId?: string | number;
  currentTotal?: number;
  readOnly?: boolean;
}

function fmt(n: number): string {
  const abs = Math.abs(n);
  const prefix = n >= 0 ? "+$" : "-$";
  return prefix + abs.toLocaleString();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  draft: { color: "#6b7280", bg: "#f3f4f6" },
  pending: { color: "#d97706", bg: "#fef3c7" },
  approved: { color: "#16a34a", bg: "#dcfce7" },
  declined: { color: "#dc2626", bg: "#fee2e2" },
  void: { color: "#6b7280", bg: "#e5e7eb" },
};

export function ChangeOrderList({ jobId, contactId, currentTotal = 0, readOnly = false }: ChangeOrderListProps) {
  const [formOpen, setFormOpen] = useState(false);
  const [update] = useUpdate();
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();

  const { data, isPending } = useGetList<ChangeOrder>("change_orders", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
    filter: { job_id: jobId },
  });

  const handleApprove = useCallback(async (co: ChangeOrder) => {
    await update("change_orders", {
      id: co.id,
      data: { status: "approved", approved_at: new Date().toISOString() },
    }, { returnPromise: true });

    await create("events", {
      data: {
        contact_id: contactId || null,
        job_id: jobId,
        event_type: "change_order_approved",
        payload: { co_id: co.id, co_number: co.co_number, cost_change: co.cost_change },
      },
    }, { returnPromise: true });

    notify(`${co.co_number} approved — job total updated`, { type: "success" });
    refresh();
  }, [jobId, contactId, update, create, notify, refresh]);

  const handleDecline = useCallback(async (co: ChangeOrder) => {
    await update("change_orders", {
      id: co.id,
      data: { status: "declined", declined_at: new Date().toISOString() },
    }, { returnPromise: true });

    await create("events", {
      data: {
        contact_id: contactId || null,
        job_id: jobId,
        event_type: "change_order_declined",
        payload: { co_id: co.id, co_number: co.co_number },
      },
    }, { returnPromise: true });

    notify(`${co.co_number} declined`, { type: "info" });
    refresh();
  }, [jobId, contactId, update, create, notify, refresh]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-1.5">
          <GitBranch className="w-4 h-4" />
          Change Orders
          {data && data.length > 0 && (
            <span className="text-xs text-muted-foreground font-normal">({data.length})</span>
          )}
        </h4>
        {!readOnly && (
          <button onClick={() => setFormOpen(true)}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-blue-600 hover:bg-blue-50">
            <Plus className="w-3 h-3" /> New CO
          </button>
        )}
      </div>

      {isPending && <p className="text-xs text-muted-foreground">Loading…</p>}

      {!isPending && (!data || data.length === 0) && (
        <p className="text-xs text-muted-foreground">No change orders.</p>
      )}

      {data && data.length > 0 && (
        <div className="space-y-1.5">
          {data.map((co) => {
            const sc = STATUS_COLORS[co.status] || STATUS_COLORS.draft;
            return (
              <Card key={co.id} className="shadow-none">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-mono text-muted-foreground">{co.co_number}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded font-medium"
                          style={{ color: sc.color, backgroundColor: sc.bg }}>
                          {co.status}
                        </span>
                      </div>
                      <p className="text-sm font-medium">{co.title}</p>
                      {co.reason && (
                        <p className="text-xs text-muted-foreground mt-0.5">Reason: {co.reason}</p>
                      )}
                      <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-0.5" style={{
                          color: co.cost_change >= 0 ? "#16a34a" : "#dc2626",
                        }}>
                          <DollarSign className="w-3 h-3" /> {fmt(Number(co.cost_change))}
                        </span>
                        {co.schedule_impact_days > 0 && (
                          <span className="flex items-center gap-0.5">
                            <Calendar className="w-3 h-3" /> +{co.schedule_impact_days}d
                          </span>
                        )}
                      </div>
                    </div>
                    {co.status === "draft" && !readOnly && (
                      <div className="flex gap-1 shrink-0 ml-2">
                        <button onClick={() => handleApprove(co)}
                          className="flex items-center gap-0.5 px-2 py-1 rounded text-xs font-medium bg-green-50 text-green-600 hover:bg-green-100">
                          <CheckCircle2 className="w-3 h-3" /> Approve
                        </button>
                        <button onClick={() => handleDecline(co)}
                          className="flex items-center gap-0.5 px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100">
                          <XCircle className="w-3 h-3" /> Decline
                        </button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ChangeOrderForm open={formOpen} onClose={() => setFormOpen(false)}
        jobId={jobId} contactId={contactId} currentTotal={currentTotal} />
    </div>
  );
}
